<template>
  <!-- "Create a Repair Job" (Android CreateBookingFragment): consumer + address → service type + notes → consumer/create-booking.
       Step 1 mirrors addUniverslQuote step 1 but calls the API directly (bookingCreateApi) so it does not react to the
       store getters that addUniverslQuote watches. -->
  <div
id="createRepairJobModal" ref="modal" class="modal fade" tabindex="-1" aria-labelledby="createRepairJobModal"
    aria-hidden="true"
>
    <div
      class="modal-dialog modal-dialog-centered mobile:!w-full mobile:!px-3 laptop:!w-[500px] mobile:!mx-auto laptop:mx-[unset] mobile:!relative laptop:!absolute mobile:!right-[unset] laptop:!right-4 mobile:!top-[unset] laptop:!top-0 !m-0 h-full"
>
      <div class="modal-content select-none">
        <!-- same look as Create Universal Quote (workOrder/addUniverslQuote.vue): intro line, stacked fields, address
             dropdown with "+ Add new address", footer pinned to the bottom; 90% tall (eval-figma.css) -->
        <div class="modal-body !p-3 !pb-0 font-roboto flex flex-col">
          <!-- eval-figma.css gives .fxModalHeader a sticky "0 -16px 0 0 #fff" shadow sized for p-[20px] bodies; this drawer
               keeps the UQ drawer's 12px (!p-3) padding, so that shadow poked 4px above the modal — drop it here. -->
          <FxModalHeader class="!shadow-none" title="Create a Repair Job" :subtitle="step === 'details' ? 'Job Details' : ''"
            :dismiss="false" @close="hideModal" />

          <!-- step 1: consumer + address (kept mounted so Back from step 2 keeps the data) -->
          <div v-show="step === 'customer'" class="uqc flex-1 flex flex-col gap-[12px] px-[6px]">
            <p class="m-0 text-center text-[14px] leading-[20px] text-[#333333]">
                Please enter Consumer's phone number<br>to create a Repair Job
              </p>
              <div>
                <label for="crjPhone" class="uqcLabel">Phone Number <span class="text-[#EE5851]">*</span></label>
                <inputComponent ref="phoneRef" v-model="phone" name="crjPhone" required mask="(###) ###-####"
                  placeholder="Enter Number" error-message="Invalid phone number" :custom-class="fieldClass" />
                <p v-if="lookedUp" class="m-0 mt-[4px] text-[12px]" :class="isConsumerFound ? 'text-[#00B7C2]' : 'text-[#999999]'">
                  {{ isConsumerFound ? (consumer.full_name || 'Consumer found') : 'Customer not found — enter the details and add an address.' }}
                </p>
              </div>
              <div>
                <label for="crjFirstName" class="uqcLabel">First Name <span class="text-[#EE5851]">*</span></label>
                <inputComponent ref="firstNameRef" v-model="first_name" name="crjFirstName" required
                  placeholder="Enter First Name" :disabled="isConsumerFound" :custom-class="fieldClass" />
              </div>
              <div>
                <label for="crjLastName" class="uqcLabel">Last Name <span class="text-[#EE5851]">*</span></label>
                <inputComponent ref="lastNameRef" v-model="last_name" name="crjLastName" required
                  placeholder="Enter Last Name" :disabled="isConsumerFound" :custom-class="fieldClass" />
              </div>
              <div>
                <label for="crjEmail" class="uqcLabel">Email</label>
                <inputComponent ref="emailRef" v-model="email" name="crjEmail" :regex="$constants.EMAIL_REGEX"
                  error-message="Please enter a valid email address" placeholder="Enter Email"
                  :disabled="isConsumerFound" :custom-class="fieldClass" />
              </div>
              <!-- Address: the consumer's saved addresses, or add a new one (the form opens below, in this modal) -->
              <div v-if="!isAddingAddress">
                <label class="uqcLabel">Address <span class="text-[#EE5851]">*</span></label>
                <FxSelect v-model="selectedAddressId" :options="addressOptions" placeholder="Select Address" :disabled="!lookedUp" />
              </div>

            <!-- new address form (Google autocomplete), same as Create Universal Quote -->
            <div v-else class="p-[12px] rounded-[10px] shadow-[0px_0px_10px_0px_#00000026]">
              <div ref="addressForm" class="flex flex-col gap-[10px]">
                <div>
                  <label class="uqcLabel">Address <span class="text-[#EE5851]">*</span></label>
                  <GoogleAutoComplete v-model="newAddress.address_1" placeholder="Address"
                    :input-class="'w-full h-[40px] px-[12px] rounded-[8px] border-1 border-[#E1E5EF] text-[14px] text-[#333333] outline-none'"
                    @place-selected="handlePlace" @address-reset="newAddress.lat = null" />
                </div>
                <div>
                  <label for="crjAddress2" class="uqcLabel">Address 2</label>
                  <inputComponent v-model="newAddress.address_2" name="crjAddress2" placeholder="Enter Address 2" :custom-class="fieldClass" />
                </div>
                <div>
                  <label for="crjState" class="uqcLabel">State <span class="text-[#EE5851]">*</span></label>
                  <inputComponent v-model="newAddress.state_id" name="crjState" required placeholder="From address" :disabled="true"
                    :custom-class="fieldClass" />
                </div>
                <div class="grid grid-cols-2 gap-[8px]">
                  <div>
                    <label for="crjCity" class="uqcLabel">City <span class="text-[#EE5851]">*</span></label>
                    <inputComponent v-model="newAddress.city_id" name="crjCity" required placeholder="From address" :disabled="true"
                      :custom-class="fieldClass" />
                  </div>
                  <div>
                    <label for="crjZip" class="uqcLabel">Zip Code <span class="text-[#EE5851]">*</span></label>
                    <inputComponent v-model="newAddress.postal_code" name="crjZip" required placeholder="From address" :disabled="true"
                      :custom-class="fieldClass" />
                  </div>
                </div>
                <!-- State / City / Zip are filled only from the picked address -->
                <p v-if="addressIncomplete" class="m-0 text-[13px] leading-[18px] text-[#EE5851]">
                  Invalid address: State, City or Zip Code is missing. Please select a complete address.
                </p>
              </div>
              <div class="flex justify-center gap-[10px] pt-[12px]">
                <button type="button" class="uqcBtnCancel" @click="cancelAddressForm">Back</button>
                <button type="button" class="uqcBtnNext" :disabled="!isNewAddressComplete" @click="saveAddress">Add</button>
              </div>
            </div>

            <div v-show="!isAddingAddress" class="uqcFooter mt-auto flex items-center justify-end gap-[10px]">
              <button type="button" class="uqcBtnCancel" @click="hideModal">Cancel</button>
              <button type="button" class="uqcBtnNext" :disabled="!isConsumerFound || !selectedAddress" @click="goToDetails">Next</button>
            </div>
          </div>

          <!-- step 2: Android service type spinner + notes -->
          <div v-if="step === 'details'" class="uqc flex-1 flex flex-col gap-[12px] px-[6px]">
            <p class="m-0 text-center text-[14px] leading-[20px] text-[#333333]">
              {{ consumerName }}<span v-if="selectedAddress"><br>{{ selectedAddress.address_1 }}</span>
            </p>
            <div>
              <label class="uqcLabel">Service Type <span class="text-[#EE5851]">*</span></label>
              <FxSelect v-model="serviceId" :options="services" label-key="title" value-key="id" placeholder="Select"
                :disabled="!services.length" />
            </div>
            <div>
              <label for="crjNotes" class="uqcLabel">Notes</label>
              <textarea id="crjNotes" v-model="notes" rows="4" maxlength="255" placeholder="Add Notes"
                class="w-full px-[12px] py-[10px] rounded-[8px] border border-[#E1E5EF] text-[14px] text-[#333333] outline-none resize-none" />
              <span class="block text-right text-[12px] text-[#999999]">{{ (notes || '').length }}/255</span>
            </div>
            <div class="uqcFooter mt-auto flex items-center justify-end gap-[10px]">
              <button type="button" class="uqcBtnCancel" :disabled="submitting" @click="step = 'customer'">Back</button>
              <button type="button" class="uqcBtnNext" :disabled="submitting" @click="submit">Done</button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Modal } from "bootstrap";
import { useToast } from "vue-toastification";
import inputComponent from "../partialComponent/inputComponent.vue";
import GoogleAutoComplete from "../partialComponent/GoogleAutoComplete.vue";
import FxModalHeader from "../nest_components/FxModalHeader.vue";
import FxSelect from "../partialComponent/FxSelect.vue";
import { bookingCreateApi } from "../../services/bookingCreateApi";
import { scheduleMessage } from "./create/scheduleMessage";

const toast = useToast();

const emptyAddress = () => ({
  address_1: null, address_2: null, street: null, city_id: null, state_id: null,
  postal_code: null, country_name: null, lat: null, long: null,
});

export default {
  components: { inputComponent, GoogleAutoComplete, FxModalHeader, FxSelect },
  emits: ["created"],
  data() {
    return {
      step: "customer",
      phone: null,
      email: null,
      first_name: null,
      last_name: null,
      lookedUp: false,
      consumer: null,
      addresses: [],
      selectedAddress: null,
      // same field look as Create Universal Quote
      fieldClass: '!h-[40px] !px-[12px] !rounded-[8px] !border-[#E1E5EF] !text-[14px] !text-[#333333] disabled:!bg-[#F7F8FA] disabled:cursor-not-allowed',
      isAddingAddress: false,
      newAddress: emptyAddress(),
      services: [],
      serviceId: null,
      notes: "",
      submitting: false,
      lookupSeq: 0,
    };
  },
  computed: {
    // address dropdown (like Create Universal Quote): saved addresses + "+ Add new address"
    addressOptions() {
      const list = (this.addresses || []).map(a => ({ label: a.address_1 + (a.address_2 ? ', ' + a.address_2 : ''), value: a.id }));
      return [...list, { label: "+ Add new address", value: "__new" }];
    },
    selectedAddressId: {
      get() {
        return this.selectedAddress ? this.selectedAddress.id : null;
      },
      set(id) {
        if (id === "__new") {
          this.selectedAddress = null;
          this.openAddressForm();
          return;
        }
        this.selectedAddress = (this.addresses || []).find(a => a.id === id) || null;
      },
    },
    isConsumerFound() {
      return (this.consumer?.company_consumer_id ?? 0) > 0;
    },
    consumerName() {
      return this.consumer?.full_name || [this.first_name, this.last_name].filter(Boolean).join(" ");
    },
    // a place was picked but Google gave no state / city / zip
    addressIncomplete() {
      const a = this.newAddress;
      return !!a.lat && !(a.city_id && a.state_id && a.postal_code);
    },
    isNewAddressComplete() {
      const a = this.newAddress;
      return !!(a.address_1 && a.city_id && a.state_id && a.postal_code && a.lat);
    },
  },
  watch: {
    // Android: the "search" button looks the consumer up; the web flow looks up as soon as 10 digits are typed (like UQ)
    phone() {
      const digits = this.phone?.replace(/\D/g, "") ?? "";
      if (digits.length === 10) this.lookupConsumer();
      else this.resetConsumer();
    },
  },
  beforeUnmount() {
    Modal.getInstance(this.$refs.modal)?.dispose();
  },
  methods: {
    showModal() {
      this.reset();
      Modal.getOrCreateInstance(this.$refs.modal, { backdrop: "static", keyboard: false }).show();
    },
    hideModal() {
      Modal.getInstance(this.$refs.modal)?.hide();
      this.reset();
    },

    // ---- consumer ----
    async lookupConsumer() {
      this.resetConsumer();
      const seq = ++this.lookupSeq;
      const res = await bookingCreateApi.consumerDetail(this.phone);
      if (seq !== this.lookupSeq) return;
      if (!res.ok) {
        toast.error(res.message || "Unable to find the consumer.");
        return;
      }
      this.consumer = res.body || {};
      this.lookedUp = true;
      this.first_name = this.consumer.first_name || null;
      this.last_name = this.consumer.last_name || null;
      this.email = this.consumer.email || null;
      this.resetRefs();
      if (this.isConsumerFound) await this.loadAddresses(this.consumer.address?.id);
    },
    // preselect: Android shows the consumer's nearest address (get-detail address) by default
    async loadAddresses(preferId) {
      const res = await bookingCreateApi.addresses(this.consumer.company_consumer_id);
      if (!res.ok) {
        toast.error(res.message || "Unable to load addresses.");
        return;
      }
      this.addresses = Array.isArray(res.body) ? res.body : [];
      this.selectAddress(preferId);
    },
    selectAddress(preferId) {
      const keepId = preferId || this.selectedAddress?.id;
      // no auto-pick (even with one address): the technician chooses; only a just-added / already chosen one stays
      this.selectedAddress = this.addresses.find(a => a.id === keepId) || null;
    },
    resetConsumer() {
      this.lookupSeq++;
      this.lookedUp = false;
      this.consumer = null;
      this.email = null;
      this.first_name = null;
      this.last_name = null;
      this.addresses = [];
      this.selectedAddress = null;
      this.isAddingAddress = false;
      this.newAddress = emptyAddress();
      this.resetRefs();
    },
    resetRefs() {
      ["phoneRef", "firstNameRef", "lastNameRef", "emailRef"].forEach(r => this.$refs[r]?.resetField?.());
    },
    // inputComponent validation for the consumer fields (names/email only matter for a new consumer)
    consumerFieldsHaveError() {
      let hasError = false;
      ["phoneRef", "firstNameRef", "lastNameRef", "emailRef"].forEach(r => {
        const c = this.$refs[r];
        if (c?.validate) {
          c.validate();
          if (c.hasError()) hasError = true;
        }
      });
      return hasError;
    },

    // ---- addresses ----
    openAddressForm() {
      this.newAddress = emptyAddress();
      this.isAddingAddress = true;
    },
    cancelAddressForm() {
      this.isAddingAddress = false;
      this.newAddress = emptyAddress();
    },
    async handlePlace(place) {
      const lat = place?.geometry?.location?.lat?.();
      const lng = place?.geometry?.location?.lng?.();
      const res = await bookingCreateApi.formattedAddress({ address: place?.formatted_address, lat, lng });
      if (!res.ok || !res.body) {
        toast.error(res.message || "Invalid address");
        return;
      }
      this.newAddress = { ...emptyAddress(), ...res.body, address_2: this.newAddress.address_2 || res.body.address_2 || "" };
    },
    async saveAddress() {
      if (!this.newAddress.lat) {
        toast.error("Please select address.");
        return;
      }
      // new consumer: consumer/store first (same as UQ step 1), then the address
      if (!this.isConsumerFound) {
        if (this.consumerFieldsHaveError() || !this.first_name?.trim() || !this.last_name?.trim()) {
          toast.error(!this.first_name?.trim() ? "First Name field is required." : !this.last_name?.trim() ? "Last Name field is required." : "Please fill all required fields.");
          return;
        }
        const created = await bookingCreateApi.storeConsumer({
          first_name: this.first_name.trim(),
          last_name: this.last_name.trim(),
          phone: this.phone,
          email: (this.email ?? "").trim(),
        });
        if (!created.ok || !created.body?.consumer?.id) {
          toast.error(created.message || "Unable to add the consumer.");
          return;
        }
        this.consumer = {
          ...this.consumer,
          company_consumer_id: created.body.consumer.id,
          consumer_id: created.body.consumer.consumer_id,
          full_name: `${this.first_name.trim()} ${this.last_name.trim()}`,
        };
      }
      const a = this.newAddress;
      const res = await bookingCreateApi.storeAddress({
        company_consumer_id: this.consumer.company_consumer_id,
        address_1: a.address_1,
        street: a.street,
        address_2: a.address_2,
        city_id: a.city_id,
        state_id: a.state_id,
        postal_code: a.postal_code,
        country: a.country_name,
        lat: a.lat,
        long: a.long,
      });
      if (!res.ok) {
        toast.error(res.message || "Unable to add the address.");
        return;
      }
      toast.success(res.message || "Address has been added successfully.");
      this.addresses = Array.isArray(res.body) ? res.body : [];
      // the response is the whole list: select the one just added
      const added = this.addresses.find(x => x.address_1 === a.address_1 && (x.address_2 || "") === (a.address_2 || ""))
        || this.addresses.find(x => x.address_1 === a.address_1);
      this.selectAddress(added?.id);
      this.isAddingAddress = false;
      this.newAddress = emptyAddress();
    },
    // ---- step 2 ----
    async goToDetails() {
      if (this.consumerFieldsHaveError() || !this.isConsumerFound) return;
      if (!this.selectedAddress) {
        toast.error("Address field is required.");
        return;
      }
      this.step = "details";
      if (!this.services.length) {
        const res = await bookingCreateApi.services();
        if (!res.ok) {
          toast.error(res.message || "Unable to load service types.");
          return;
        }
        this.services = Array.isArray(res.body) ? res.body : [];
      }
    },
    // Android validateAndSetDataData → checkTechValidate → (confirm) → createBooking
    async submit() {
      if (this.submitting) return;
      if (!this.selectedAddress) {
        toast.error("Address field is required.");
        return;
      }
      if (!this.serviceId) {
        toast.error("Please select Service Type");
        return;
      }
      this.submitting = true;
      const check = await bookingCreateApi.validateTechnician();
      this.submitting = false;
      const count = check.ok ? (check.body?.count ?? 0) : 0;
      if (count > 0) {
        const message = scheduleMessage(check.body?.items)
          || "You already have one or more active jobs. Do you want to create another job?";
        this.$confirmDialog.show({
          title: "Confirmation",
          text: message,
          message,
          buttonText: "Yes",
          onConfirm: () => this.create(),
        });
        return;
      }
      this.create();
    },
    async create() {
      const params = {
        consumer_id: this.consumer.company_consumer_id,
        address_id: this.selectedAddress.id,
        service_id: this.serviceId,
      };
      const notes = (this.notes || "").trim();
      if (notes) params.notes = notes;

      this.submitting = true;
      const res = await bookingCreateApi.create(params);
      this.submitting = false;
      if (!res.ok) {
        this.handleFailure(res);
        return;
      }
      const booking = res.body?.booking;
      toast.success("Service & Repair job has been created successfully.");
      this.$emit("created", booking);
      this.hideModal();
      if (booking?.id) this.$router.push(`/job/detail/${booking.id}`);
    },
    // single-active-record guard → offer to open the active job; otherwise toast the API message
    handleFailure(res) {
      const guard = res.activeRecord;
      const record = guard?.active_record;
      if (record) {
        this.$dialog.show({
          title: "Dispositioned Leads",
          text: guard.message || res.message,
          buttonText: record.button_text || "Resolve",
          onConfirm: () => {
            if (!record.id) return;
            this.hideModal();
            if (record.type === "appointment") this.$router.push(`/appointment/detail/${record.id}`);
            else if (record.type === "order") this.$router.push(`/replacement-quote/detail/${record.id}`);
            else this.$router.push(`/job/detail/${record.id}`);
          },
        });
        return;
      }
      toast.error((typeof res.body === "string" && res.body) || res.message || "Unable to create the repair job.");
    },

    reset() {
      this.step = "customer";
      this.phone = null;
      this.resetConsumer();
      this.serviceId = null;
      this.notes = "";
      this.submitting = false;
    },
  },
};
</script>
